import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, unref as _unref, createBlock as _createBlock, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "rubrica-tabla" }
const _hoisted_2 = {
  key: 0,
  class: "tabla-scroll"
}
const _hoisted_3 = { class: "tabla-rubrica" }
const _hoisted_4 = { class: "celda-grado" }
const _hoisted_5 = { class: "celda-escala" }
const _hoisted_6 = { key: 1 }
const _hoisted_7 = {
  key: 1,
  class: "bloque"
}
const _hoisted_8 = { class: "tabla-scroll" }
const _hoisted_9 = { class: "tabla-rubrica" }
const _hoisted_10 = { class: "celda-grado" }
const _hoisted_11 = { class: "bloque" }
const _hoisted_12 = { class: "conversion" }
const _hoisted_13 = { class: "conversion" }
const _hoisted_14 = {
  key: 2,
  class: "tabla-scroll"
}
const _hoisted_15 = { class: "tabla-rubrica" }
const _hoisted_16 = { class: "celda-grado" }
const _hoisted_17 = { class: "celda-centro" }
const _hoisted_18 = { class: "celda-escala" }
const _hoisted_19 = {
  key: 3,
  class: "vacio"
}

import { computed } from 'vue'
import type { RubricaCriterio, RubricaDocumental, GradoDocumental } from '@/interfaces/CriterioEvaluacion'
import {
  esRubricaDocumental,
  esRubricaCuantitativa,
  esRubricaOpinion
} from '@/interfaces/CriterioEvaluacion'
import RubricaTablaOpinion from '@/components/RubricaTablaOpinion.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'RubricaTabla',
  props: {
    rubrica: {},
    notaActual: {}
  },
  setup(__props: any) {

/**
 * Tabla de una rúbrica, según su tipo de información. Es la misma que se ve en
 * Gestión de Rúbricas → Ver y la que acompaña a la calificación: se comparte
 * para que el que califica lea exactamente la rúbrica que se le definió.
 *
 * `notaActual` resalta la banda en la que cae la nota que se está poniendo.
 */
const props = __props

const gradosDocumental = computed<GradoDocumental[]>(() => {
  const r = props.rubrica as RubricaDocumental | null | undefined

  return Array.isArray(r?.grados) ? r.grados : []
})

/**
 * Una rúbrica documental es la que reparte descriptores por grado. Se acepta
 * aunque le falte la lista `criterios` —las columnas salen entonces de los
 * propios descriptores— para no dejar en blanco una rúbrica editada a mano.
 */
const esDocumental = computed(() => {
  const r = props.rubrica
  if (!r || esRubricaOpinion(r) || esRubricaCuantitativa(r)) return false

  return esRubricaDocumental(r) || gradosDocumental.value.some(g => !!g.descriptores)
})

const columnasDocumental = computed<string[]>(() => {
  const declaradas = (props.rubrica as RubricaDocumental | null | undefined)?.criterios
  if (Array.isArray(declaradas) && declaradas.length) return declaradas

  // Sin lista declarada, las columnas son las claves de los descriptores.
  const claves = new Set<string>()
  for (const g of gradosDocumental.value) {
    Object.keys(g.descriptores ?? {}).forEach(k => claves.add(k))
  }

  return [...claves]
})

function esGradoActivo(min?: number | null, max?: number | null): boolean {
  const nota = props.notaActual
  if (nota == null || min == null || max == null) return false
  return nota >= min && nota <= max
}

/** "4.5 a 5.0" — se conserva un decimal para que las bandas se lean parejas. */
function formatoRango(min?: number, max?: number): string {
  if (min == null || max == null) return '—'
  return `${formatoNumero(min)} a ${formatoNumero(max)}`
}

function formatoNumero(v: number): string {
  // 5 → "5.0" · 4.49 → "4.49" (no se recorta el segundo decimal si existe)
  return Number.isInteger(v) ? v.toFixed(1) : String(v)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (esDocumental.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("table", _hoisted_3, [
            _createElementVNode("thead", null, [
              _createElementVNode("tr", null, [
                _cache[0] || (_cache[0] = _createElementVNode("th", { class: "col-grado" }, "GRADO CUMPLIMIENTO", -1)),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(columnasDocumental.value, (c) => {
                  return (_openBlock(), _createElementBlock("th", { key: c }, _toDisplayString(c), 1))
                }), 128)),
                _cache[1] || (_cache[1] = _createElementVNode("th", { class: "col-escala" }, "ESCALA CUANTITATIVA", -1))
              ])
            ]),
            _createElementVNode("tbody", null, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(gradosDocumental.value, (g, i) => {
                return (_openBlock(), _createElementBlock("tr", {
                  key: i,
                  class: _normalizeClass({ 'fila-activa': esGradoActivo(g.rango_min, g.rango_max) })
                }, [
                  _createElementVNode("td", _hoisted_4, _toDisplayString(g.grado), 1),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(columnasDocumental.value, (c) => {
                    return (_openBlock(), _createElementBlock("td", { key: c }, _toDisplayString(g.descriptores?.[c] || '—'), 1))
                  }), 128)),
                  _createElementVNode("td", _hoisted_5, _toDisplayString(formatoRango(g.rango_min, g.rango_max)), 1)
                ], 2))
              }), 128))
            ])
          ])
        ]))
      : (_ctx.rubrica && _unref(esRubricaOpinion)(_ctx.rubrica))
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
            (_ctx.rubrica.grados?.length)
              ? (_openBlock(), _createBlock(RubricaTablaOpinion, {
                  key: 0,
                  rubrica: _ctx.rubrica
                }, null, 8, ["rubrica"]))
              : _createCommentVNode("", true),
            (_ctx.rubrica.muestreo?.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                  _cache[3] || (_cache[3] = _createElementVNode("h5", { class: "bloque-titulo" }, "Diseño de muestreo", -1)),
                  _createElementVNode("div", _hoisted_8, [
                    _createElementVNode("table", _hoisted_9, [
                      _cache[2] || (_cache[2] = _createElementVNode("thead", null, [
                        _createElementVNode("tr", null, [
                          _createElementVNode("th", null, "GRUPO DE INTERÉS"),
                          _createElementVNode("th", null, "TIPO DE MUESTREO"),
                          _createElementVNode("th", null, "CRITERIOS"),
                          _createElementVNode("th", null, "TÉCNICA")
                        ])
                      ], -1)),
                      _createElementVNode("tbody", null, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.rubrica.muestreo, (m, i) => {
                          return (_openBlock(), _createElementBlock("tr", { key: i }, [
                            _createElementVNode("td", _hoisted_10, _toDisplayString(m.grupo_interes), 1),
                            _createElementVNode("td", null, _toDisplayString(m.tipo_muestreo), 1),
                            _createElementVNode("td", null, _toDisplayString(m.criterios || '—'), 1),
                            _createElementVNode("td", null, _toDisplayString(m.tecnica), 1)
                          ]))
                        }), 128))
                      ])
                    ])
                  ])
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_11, [
              _cache[6] || (_cache[6] = _createElementVNode("h5", { class: "bloque-titulo" }, "Regla de conversión a 0–5", -1)),
              _createElementVNode("p", _hoisted_12, [
                _cache[4] || (_cache[4] = _createElementVNode("strong", null, "Fórmula:", -1)),
                _createTextVNode(" " + _toDisplayString(_ctx.rubrica.conversion?.formula || '—'), 1)
              ]),
              _createElementVNode("p", _hoisted_13, [
                _cache[5] || (_cache[5] = _createElementVNode("strong", null, "Escala:", -1)),
                _createTextVNode(" " + _toDisplayString(_ctx.rubrica.conversion?.escala || '—'), 1)
              ])
            ])
          ]))
        : (_ctx.rubrica && _unref(esRubricaCuantitativa)(_ctx.rubrica))
          ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
              _createElementVNode("table", _hoisted_15, [
                _cache[7] || (_cache[7] = _createElementVNode("thead", null, [
                  _createElementVNode("tr", null, [
                    _createElementVNode("th", { class: "col-grado" }, "GRADO CUMPLIMIENTO"),
                    _createElementVNode("th", null, "CRITERIO"),
                    _createElementVNode("th", null, "DESCRIPCIÓN"),
                    _createElementVNode("th", { class: "col-escala" }, "ESCALA CUANTITATIVA")
                  ])
                ], -1)),
                _createElementVNode("tbody", null, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.rubrica.niveles, (n, i) => {
                    return (_openBlock(), _createElementBlock("tr", {
                      key: i,
                      class: _normalizeClass({ 'fila-activa': esGradoActivo(n.rango_min, n.rango_max) })
                    }, [
                      _createElementVNode("td", _hoisted_16, _toDisplayString(n.grado || n.label || '—'), 1),
                      _createElementVNode("td", _hoisted_17, _toDisplayString(n.tendencia || '—'), 1),
                      _createElementVNode("td", null, _toDisplayString(n.descripcion || '—'), 1),
                      _createElementVNode("td", _hoisted_18, _toDisplayString(formatoRango(n.rango_min, n.rango_max)), 1)
                    ], 2))
                  }), 128))
                ])
              ])
            ]))
          : (_openBlock(), _createElementBlock("p", _hoisted_19, "No hay una rúbrica que mostrar."))
  ]))
}
}

})